import { SECTION_HARD_ENCODED_BYTES, SECTION_WARNING_ENCODED_BYTES } from "@workith/shared";
import * as Y from "yjs";

/**
 * Section size policy for `sectioned-v1` documents.
 *
 * The boundaries themselves live in `@workith/shared` because the server's
 * `needs_rebalance` flag and the maintenance rebalancer have to agree with what
 * this guard warns on. Re-exported under the names the editor uses so the policy
 * has exactly one definition.
 */
export const SECTION_WARNING_BYTES = SECTION_WARNING_ENCODED_BYTES;
export const SECTION_ERROR_BYTES = SECTION_HARD_ENCODED_BYTES;

export type SectionSizeLevel = "ok" | "warning" | "error";

export function classifySectionSize(encodedBytes: number): SectionSizeLevel {
  if (encodedBytes >= SECTION_ERROR_BYTES) return "error";
  if (encodedBytes >= SECTION_WARNING_BYTES) return "warning";
  return "ok";
}

/** Encoded Yjs state size, which is what a room's initial sync has to carry. */
export function measureSectionEncodedBytes(document: Y.Doc): number {
  return Y.encodeStateAsUpdate(document).byteLength;
}

export type InsertionGuardDecision =
  | { kind: "allow" }
  | { kind: "split"; paragraphs: string[] }
  | { kind: "reject"; reason: "oversized-atomic-block" };

interface InsertionGuardInput {
  /** Encoded size of the single top-level block the insertion would create. */
  encodedBytes: number;
  /** True for code blocks, tables and custom nodes that cannot be split safely. */
  isAtomicBlock: boolean;
  plainText: string;
}

/**
 * Pre-checks a paste or import before it becomes one top-level block.
 *
 * Splitting is only offered where it preserves meaning, so an oversized code
 * block, table or custom node is refused outright rather than cut in half.
 */
export function evaluateInsertionGuard({
  encodedBytes,
  isAtomicBlock,
  plainText,
}: InsertionGuardInput): InsertionGuardDecision {
  if (encodedBytes < SECTION_WARNING_BYTES) {
    return { kind: "allow" };
  }

  if (isAtomicBlock) {
    return { kind: "reject", reason: "oversized-atomic-block" };
  }

  const paragraphs = splitPlainTextIntoParagraphs(plainText);
  if (paragraphs.length < 2) {
    return { kind: "reject", reason: "oversized-atomic-block" };
  }

  return { kind: "split", paragraphs };
}

/** Splits on blank lines, falling back to single lines, dropping empty entries. */
export function splitPlainTextIntoParagraphs(plainText: string): string[] {
  const byBlankLine = toTrimmedNonEmpty(plainText.split(/\n\s*\n/));
  if (byBlankLine.length > 1) return byBlankLine;
  return toTrimmedNonEmpty(plainText.split("\n"));
}

function toTrimmedNonEmpty(parts: string[]): string[] {
  return parts.map((part) => part.trim()).filter((part) => part.length > 0);
}

export interface BlockSplit<TBlock> {
  kept: TBlock[];
  moved: TBlock[];
}

/**
 * Splits top-level blocks at `index`, moving that block and everything after it.
 *
 * Returns null when the split would leave either side empty: a section is never
 * emptied and a new section is never created without content, because no
 * ProseMirror block is ever cut open to hit a size target.
 */
export function splitBlocksAtIndex<TBlock>(
  blocks: TBlock[],
  index: number
): BlockSplit<TBlock> | null {
  if (!Number.isInteger(index) || index <= 0 || index >= blocks.length) return null;
  return { kept: blocks.slice(0, index), moved: blocks.slice(index) };
}

/** ProseMirror document position where the top-level block at `index` starts. */
export function topLevelBlockOffset(nodeSizes: number[], index: number): number {
  return nodeSizes.slice(0, index).reduce((total, size) => total + size, 0);
}

interface OversizedSectionReport {
  documentId: string;
  sectionId: string;
  encodedBytes: number;
}

/**
 * Reports a section past the hard limit. Only identifiers and byte counts are
 * logged - never Yjs payloads or document text. The matching `needs_rebalance`
 * flag is written server side when the room state is stored, so this telemetry is
 * what lets operations page on a client that is already over the limit.
 */
export function reportOversizedSection({
  documentId,
  sectionId,
  encodedBytes,
}: OversizedSectionReport): void {
  console.error("collaboration.section_over_hard_limit", {
    documentId,
    sectionId,
    encodedBytes,
    hardLimitBytes: SECTION_ERROR_BYTES,
    needsRebalance: true,
  });
}
